import { Component, Emit, Inject, Model, Prop, Vue, Watch } from 'vue-property-decorator';
import GoModal from '@/core/go-modal/go-modal.vue';
import type { OATH2_PROVIDER } from '@/constants';
import { Account, Oath2AppInfo } from '@/shared/model/scrap-agent.model';
import ScrapAgentService from '@/service/scrap-agent.service';
import { v4 as uuidv4 } from 'uuid';

@Component({
  components: {
    'go-modal': GoModal,
  },
})
export default class ChannelLoginModal extends Vue {
  @Model('open', { type: Boolean, required: true }) open!: boolean;
  @Prop({ type: String }) readonly channelType: string;
  @Prop({ type: String }) readonly channelName: string;
  @Prop({ type: String }) readonly channelImageUri: string;

  @Inject('scrapAgentService')
  public scrapAgentService: () => ScrapAgentService;

  @Emit('modal:close') close() {
    this.state.formData = {
      username: '',
      password: '',
    };
  }

  @Emit('modal:ok') ok(payload: any) {}

  state = {
    appInfo: null as Oath2AppInfo | null,
    oath2: false,
    oath2Provider: null as OATH2_PROVIDER | null,
    loading: false,
    messageProcessed: false,
    authState: null,
    formData: {
      username: '',
      password: '',
    },
    showPassword: false,
  };

  beforeDestroy() {
    window.removeEventListener('message', this.receiveMessage);
  }

  @Watch('channelType', { immediate: true })
  onChannelTypeChange(newChannelType: string) {
    if (!newChannelType) return;
    window.removeEventListener('message', this.receiveMessage);
    window.addEventListener('message', this.receiveMessage, false);
    switch (newChannelType) {
      case 'GOOGLE_DRIVE':
      case 'YOUTUBE':
      case 'GMAIL': {
        this.state = {
          ...this.state,
          oath2: true,
          oath2Provider: 'GOOGLE',
          loading: false,
        };
        break;
      }
      case 'ONE_DRIVE':
      case 'OUTLOOK': {
        this.state = {
          ...this.state,
          oath2: true,
          oath2Provider: 'MICROSOFT',
          loading: false,
        };
        break;
      }
      case 'DROP_BOX': {
        this.state = {
          ...this.state,
          oath2: true,
          oath2Provider: 'DROP_BOX',
          loading: false,
        };
        break;
      }
      case 'FACEBOOK': {
        this.state = {
          ...this.state,
          oath2: true,
          oath2Provider: 'FACEBOOK',
          loading: false,
        };
        break;
      }
      case 'X_TWITTER': {
        this.state = {
          ...this.state,
          oath2: true,
          oath2Provider: 'X_TWITTER',
          loading: false,
        };
        break;
      }
      case 'WORDPRESS': {
        this.state = {
          ...this.state,
          oath2: true,
          oath2Provider: 'WORDPRESS',
          loading: false,
        };
        break;
      }
      case 'DAUM':
      case 'NAVER':
      case 'BRUNCH':
      case 'NAVER_BLOG': {
        this.state = {
          ...this.state,
          appInfo: null,
          oath2: false,
          oath2Provider: null,
          loading: false,
        };
        break;
      }
    }
    if (this.state.oath2) {
      this.fetchAppInfo(this.channelType);
    }
  }

  fetchAppInfo(channelType: string) {
    this.scrapAgentService()
      .getAppInfo(channelType)
      .then(res => {
        if (res.success && res.body.length) {
          this.state.appInfo = res.body[0];
        }
      });
  }

  receiveMessage(event: MessageEvent) {
    if (this.state.messageProcessed) {
      return;
    }
    console.log(event.data, 'event.data');
    console.log(this.state.authState, 'authState');
    // Verify that the message payload contains the expected state and authorization_code.
    if (event.data && event.data.authorization_code && event.data.state === this.state.authState) {
      this.state.messageProcessed = true;
      const authCode = event.data.authorization_code;
      this.handleLoginOath2(authCode);
    } else {
      this.state.messageProcessed = false;
      console.warn('Ignoring unexpected message payload:', event.data);
    }
  }

  handleLoginOath2(authorizationCode: string) {
    const userKey = this.$store.getters.userIdentity?.id || JSON.parse(localStorage.getItem('localStorageUser'))?.id;
    const payload = {
      userKey,
      authorizationCode: authorizationCode,
      chanelName: this.channelType,
      clientId: this.state.appInfo?.clientId,
      appId: this.state.appInfo?.appId,
      clientSecret: this.state.appInfo?.clientSecret,
      redirectUrl: window.location.origin + '/api/public/goAuth',
      scope: this.state.appInfo?.scope,
    };
    this.handleLogin(payload);
  }

  handleLoginByUsernameAndPassword() {
    const userKey = this.$store.getters.userIdentity?.id || JSON.parse(localStorage.getItem('localStorageUser'))?.id;
    const payload = {
      userKey,
      accountName: this.state.formData.username,
      password: this.state.formData.password,
      chanelName: this.channelType,
    };
    this.handleLogin(payload);
  }

  handleLogin(payload: Account) {
    this.scrapAgentService()
      .addUserAccount(payload)
      .then(res => {
        console.log(res);
        const bodyRes = res.data?.body?.body;
        if (bodyRes.success) {
          this.state.formData = {
            username: '',
            password: '',
          };
          this.$bvToast.toast('Authorization Account Success', {
            toaster: 'b-toaster-bottom-right',
            title: 'Authorize success',
            variant: 'success',
            solid: true,
            autoHideDelay: 5000,
          });
          this.ok(bodyRes.body);
        }
      });
  }

  ssoWithGoogle() {
    this.state.authState = 'GOID_' + uuidv4().toString();
    const authUrl =
      `https://accounts.google.com/o/oauth2/v2/auth` +
      `?access_type=offline` +
      `&client_id=${this.state.appInfo?.clientId}` +
      `&response_type=code` +
      `&redirect_uri=${window.location.origin + '/api/public/goAuth'}` +
      `&scope=${this.state.appInfo?.scope}` +
      `&state=${this.state.authState}` +
      `&prompt=consent`;
    console.log(authUrl);
    window.open(authUrl, 'Google Login', 'width=600,height=600');
  }

  ssoWithMicrosoft() {
    this.state.authState = 'GOID_' + uuidv4().toString();
    const authUrl =
      `https://login.microsoftonline.com/common/oauth2/v2.0/authorize` +
      `?response_type=code` +
      `&client_id=${this.state.appInfo?.clientId}` +
      `&redirect_uri=${window.location.origin + '/api/public/goAuth'}` +
      `&response_mode=query` +
      `&scope=${encodeURIComponent(this.state.appInfo?.scope)}` +
      `&state=${this.state.authState}`;

    window.open(authUrl, 'Microsoft Login', 'width=600,height=600');
  }

  ssoWithFacebook() {
    this.state.authState = 'GOID_' + uuidv4().toString();
    const authUrl =
      `https://www.facebook.com/v22.0/dialog/oauth` +
      `?response_type=code` +
      `&client_id=${this.state.appInfo?.clientId}` +
      `&redirect_uri=${window.location.origin + '/api/public/goAuth'}` +
      `&scope=${encodeURIComponent(this.state.appInfo?.scope)}` +
      `&state=${this.state.authState}`;

    window.open(authUrl, 'Facebook Login', 'width=600,height=600');
  }

  ssoWithXTwitter() {
    this.state.authState = 'GOID_' + uuidv4().toString();
    const authUrl =
      `https://x.com/i/oauth2/authorize` +
      `?response_type=code` +
      `&client_id=${this.state.appInfo?.clientId}` +
      `&redirect_uri=${window.location.origin + '/api/public/goAuth'}` +
      `&scope=${this.state.appInfo?.scope}` +
      `&state=${this.state.authState}` +
      `&code_challenge=ycbxnR_xs-RK8mrNhtv0ZymGWdbNfBn34BuP0N3Deik` +
      `&code_challenge_method=S256`;

    window.open(authUrl, 'X(Twitter) Login', 'width=600,height=600');
  }

  ssoWithDropbox() {
    this.state.authState = 'GOID_' + uuidv4().toString();
    const authUrl =
      `https://www.dropbox.com/oauth2/authorize` +
      `?response_type=code` +
      `&client_id=${this.state.appInfo?.clientId}` +
      `&redirect_uri=${window.location.origin + '/api/public/goAuth'}` +
      `&token_access_type=offline` +
      `&force_reauthentication=true` +
      `&scope=${this.state.appInfo?.scope}` +
      `&state=${this.state.authState}`;
    window.open(authUrl, 'Dropbox Login', 'width=600,height=600');
  }

  ssoWithWordpress() {
    this.state.authState = 'GOID_' + uuidv4().toString();
    const authUrl =
      `https://public-api.wordpress.com/oauth2/authorize` +
      `?client_id=${this.state.appInfo?.clientId}` +
      `&redirect_uri=${window.location.origin + '/api/public/goAuth'}` +
      `&response_type=code` +
      `&state=${this.state.authState}`;
    window.open(authUrl, 'Wordpress Login', 'width=600,height=600');
  }
}
